import { createFileRoute, useRouterState } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { User, Bell, Globe, Palette, Shield, Key, Download, Camera, Trash2, Check, Zap, Activity, Share2 } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar } from '@/components/ui/avatar';
import { useAuth } from '@/hooks/useAuth';
import { useUpdateProfile, useUploadAvatar, useDeleteAvatar, useChangePassword } from '@/hooks/data/useProfile';
import { PasswordField } from '@/components/ui/PasswordField';
import {
  useUpdateAppearance,
  useUploadBackground,
  parseAppearance,
} from '@/hooks/data/useAppearance';
import {
  APPEARANCE_PRESETS,
  type AppearancePrefs,
} from '@/lib/appearancePresets';
import { AvatarCropDialog } from '@/components/AvatarCropDialog';
import { contrastRatio, suggestReadableAccent } from '@/lib/colorUtils';
import { AutomationPanel } from '@/components/automation/AutomationPanel';
import { ActivityPanel } from '@/components/admin/ActivityPanel';
import { CalendarSharingPanel } from '@/components/settings/CalendarSharingPanel';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { DEFAULT_LOCALE, liveLocales, LOCALES } from '@4m/shared';
import { setLocale } from '@/i18n';

export const Route = createFileRoute('/_app/settings')({
  component: SettingsPage,
});

function SettingsPage() {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const isAdmin = profile?.role === 'admin';

  // Deep-link: the account menu's "Passwort ändern" links to /settings#security
  // so it opens the Sicherheit tab directly. useRouterState keeps it reactive
  // even when the user is already on /settings.
  const hash = useRouterState({ select: (s) => s.location.hash });
  const [tab, setTab] = useState<string>(hash === 'security' ? 'security' : 'profile');
  useEffect(() => {
    if (hash === 'security') setTab('security');
  }, [hash]);

  return (
    <section className="space-y-6">
      <header>
        <h2 className="font-display text-2xl text-ink">
          {t('settings.title', 'Einstellungen')}
        </h2>
        <p className="text-sm text-ink-3">
          {t('settings.subtitle', 'Profil, Benachrichtigungen, Sprache und Sicherheit.')}
        </p>
      </header>

      <Tabs value={tab} onValueChange={setTab} className="space-y-6">
        <TabsList className="flex-wrap">
          <TabsTrigger value="profile" className="gap-2">
            <User className="h-3.5 w-3.5" /> {t('settings.tab.profile', 'Profil')}
          </TabsTrigger>
          <TabsTrigger value="notifications" className="gap-2">
            <Bell className="h-3.5 w-3.5" /> {t('settings.tab.notifications', 'Benachrichtigungen')}
          </TabsTrigger>
          <TabsTrigger value="language" className="gap-2">
            <Globe className="h-3.5 w-3.5" /> {t('settings.tab.language', 'Sprache')}
          </TabsTrigger>
          <TabsTrigger value="theme" className="gap-2">
            <Palette className="h-3.5 w-3.5" /> {t('settings.tab.theme', 'Design')}
          </TabsTrigger>
          <TabsTrigger value="security" className="gap-2">
            <Shield className="h-3.5 w-3.5" /> {t('settings.tab.security', 'Sicherheit')}
          </TabsTrigger>
          <TabsTrigger value="calendar_share" className="gap-2">
            <Share2 className="h-3.5 w-3.5" /> {t('settings.tab.calendar_share', 'Kalender teilen')}
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="automation" className="gap-2">
              <Zap className="h-3.5 w-3.5" /> {t('settings.tab.automation', 'Automatisierung')}
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="activity" className="gap-2">
              <Activity className="h-3.5 w-3.5" /> {t('settings.tab.activity', 'Aktivität')}
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="api" className="gap-2">
              <Key className="h-3.5 w-3.5" /> {t('settings.tab.api', 'API-Schlüssel')}
            </TabsTrigger>
          )}
          <TabsTrigger value="export" className="gap-2">
            <Download className="h-3.5 w-3.5" /> {t('settings.tab.export', 'Datenexport')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="profile">
          <ProfilePanel />
        </TabsContent>
        <TabsContent value="notifications">
          <NotificationsPanel />
        </TabsContent>
        <TabsContent value="language">
          <LanguagePanel />
        </TabsContent>
        <TabsContent value="theme">
          <ThemePanel />
        </TabsContent>
        <TabsContent value="security">
          <SecurityPanel />
        </TabsContent>
        <TabsContent value="calendar_share">
          <CalendarSharingPanel />
        </TabsContent>
        {isAdmin && (
          <TabsContent value="automation">
            <AutomationPanel />
          </TabsContent>
        )}
        {isAdmin && (
          <TabsContent value="activity">
            <ActivityPanel />
          </TabsContent>
        )}
        {isAdmin && (
          <TabsContent value="api">
            <ApiKeysPanel />
          </TabsContent>
        )}
        <TabsContent value="export">
          <ExportPanel />
        </TabsContent>
      </Tabs>
    </section>
  );
}

function ProfilePanel() {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const update = useUpdateProfile();
  const uploadAvatar = useUploadAvatar();
  const deleteAvatar = useDeleteAvatar();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [fullName, setFullName] = useState(profile?.full_name ?? '');
  const [title, setTitle] = useState(profile?.title ?? '');
  const [email, setEmail] = useState(profile?.email ?? '');
  const [phone, setPhone] = useState(profile?.phone ?? '');
  // Batch ZY — moved BEFORE the `if (!profile) return null` early return.
  // Was previously declared later in the function, which made the hook
  // count differ between first render (profile undefined → return null →
  // 8 hooks) and subsequent renders (profile loaded → continues past
  // the if → 9 hooks). That triggers React error #310 ("rendered more
  // hooks than during the previous render") whenever the parent
  // re-renders with a brief profile=undefined window (e.g. idle timeout
  // recheck, manual logout flow).
  const [pendingCrop, setPendingCrop] = useState<File | null>(null);

  useEffect(() => {
    if (!profile) return;
    setFullName(profile.full_name ?? '');
    setTitle(profile.title ?? '');
    setEmail(profile.email ?? '');
    setPhone(profile.phone ?? '');
  }, [profile?.id, profile?.full_name, profile?.title, profile?.email, profile?.phone]);

  if (!profile) return null;

  const onSave = async () => {
    try {
      await update.mutateAsync({
        fullName: fullName.trim() || undefined,
        title: title.trim() ? title.trim() : null,
        email: email.trim() ? email.trim() : null,
        phone: phone.trim() ? phone.trim() : null,
      });
      toast.success(t('settings.profile.saved', 'Profil gespeichert'));
    } catch (cause) {
      toast.error((cause as Error).message);
    }
  };

  const onAvatarPick = () => fileInputRef.current?.click();
  const onFileChosen = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    // Open the crop dialog instead of uploading the raw file
    setPendingCrop(file);
  };

  const onCropConfirm = async (cropped: File) => {
    try {
      await uploadAvatar.mutateAsync(cropped);
      toast.success(t('settings.profile.avatar_saved', 'Profilbild aktualisiert'));
      setPendingCrop(null);
    } catch (cause) {
      toast.error((cause as Error).message);
    }
  };
  const onAvatarDelete = async () => {
    try {
      await deleteAvatar.mutateAsync();
      toast.success(t('settings.profile.avatar_removed', 'Profilbild entfernt'));
    } catch (cause) {
      toast.error((cause as Error).message);
    }
  };

  return (
    <div className="fm-card space-y-6 p-6">
      <div className="flex items-center gap-5">
        <Avatar
          name={profile.full_name}
          size="lg"
          src={profile.avatar_url}
          className="!h-20 !w-20 !text-2xl"
        />
        <div className="flex-1">
          <p className="font-display text-xl text-ink">{profile.full_name}</p>
          <p className="text-xs uppercase tracking-[0.08em] text-ink-3">
            <span className="font-mono">{profile.agent_id}</span> · {profile.role}
          </p>
        </div>
        <div className="flex flex-col gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={onFileChosen}
          />
          <button
            type="button"
            onClick={onAvatarPick}
            disabled={uploadAvatar.isPending}
            className="inline-flex items-center gap-1.5 rounded-md border border-line bg-card px-3 py-1.5 text-xs font-semibold text-ink hover:bg-paper-2 disabled:opacity-50"
          >
            <Camera className="h-3.5 w-3.5" />
            {uploadAvatar.isPending
              ? t('settings.profile.uploading', 'Lade …')
              : t('settings.profile.change_avatar', 'Bild ändern')}
          </button>
          {profile.avatar_url && (
            <button
              type="button"
              onClick={onAvatarDelete}
              disabled={deleteAvatar.isPending}
              className="inline-flex items-center gap-1.5 rounded-md border border-line bg-card px-3 py-1.5 text-xs font-medium text-[hsl(var(--danger))] hover:bg-paper-2 disabled:opacity-50"
            >
              <Trash2 className="h-3.5 w-3.5" />
              {t('settings.profile.remove_avatar', 'Entfernen')}
            </button>
          )}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label={t('settings.profile.full_name', 'Vor- und Nachname')}
          value={fullName}
          onChange={setFullName}
          placeholder="Max Mustermann"
        />
        <Field
          label={t('settings.profile.title', 'Titel / Funktion')}
          value={title}
          onChange={setTitle}
          placeholder="Senior-Makler · Wien Mitte"
        />
        <Field
          label={t('settings.profile.email', 'E-Mail')}
          value={email}
          onChange={setEmail}
          type="email"
          placeholder="m.mustermann@4m-immo.at"
        />
        <Field
          label={t('settings.profile.phone', 'Telefon')}
          value={phone}
          onChange={setPhone}
          // `tel`, not the default `text`: the RTL rule in styles/index.css
          // keeps tel inputs left-to-right. As plain text the groups of a
          // number are reordered in Arabic/Hebrew ("4567 123 660 43+").
          type="tel"
          placeholder="+43 660 123 4567"
        />
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={onSave}
          disabled={update.isPending}
          className="inline-flex items-center gap-1.5 rounded-md bg-ink px-4 py-2 text-sm font-semibold text-paper hover:bg-ink/90 disabled:opacity-60"
        >
          <Check className="h-3.5 w-3.5" />
          {update.isPending
            ? t('settings.profile.saving', 'Speichern …')
            : t('settings.profile.save', 'Speichern')}
        </button>
      </div>

      <AvatarCropDialog
        open={!!pendingCrop}
        file={pendingCrop}
        onCancel={() => setPendingCrop(null)}
        onConfirm={onCropConfirm}
      />
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
}) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-semibold uppercase text-ink-3 tracking-[0.08em]">
        {label}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-md border border-line bg-card px-3 py-2 text-sm text-ink outline-none focus:border-gold focus:ring-2 focus:ring-gold/30"
      />
    </label>
  );
}

function NotificationsPanel() {
  const { t } = useTranslation();
  const [briefing, setBriefing] = useState(true);
  const [smsReminder, setSmsReminder] = useState(true);
  const [assignment, setAssignment] = useState(true);
  const push = usePushNotifications();

  return (
    <div className="fm-card p-6 space-y-3">
      <div className="rounded-md border border-line bg-paper-2 p-3">
        <p className="font-medium text-ink">
          {t('settings.notif.push_title', 'Push-Benachrichtigungen auf diesem Gerät')}
        </p>
        <p className="mt-0.5 text-[12px] text-ink-3">
          {t(
            'settings.notif.push_hint',
            'Benachrichtigt Sie auch dann, wenn die App nicht offen ist. Auf iPhone: erst zur Startseite hinzufügen, dann hier aktivieren.',
          )}
        </p>
        <div className="mt-2 flex items-center gap-2">
          {push.state === 'unsupported' ? (
            <span className="text-xs text-ink-3">
              {t('settings.notif.push_unsupported', 'Vom Browser nicht unterstützt.')}
            </span>
          ) : push.isSubscribed ? (
            <button
              type="button"
              onClick={push.disable}
              className="rounded-md border border-line bg-card px-3 py-1.5 text-xs font-medium hover:bg-paper-3"
            >
              {t('settings.notif.push_disable', 'Deaktivieren')}
            </button>
          ) : (
            <button
              type="button"
              onClick={push.enable}
              disabled={push.state === 'subscribing'}
              className="rounded-md bg-ink px-3 py-1.5 text-xs font-semibold text-paper hover:bg-ink/90 disabled:opacity-60"
            >
              {push.state === 'subscribing'
                ? t('settings.notif.push_enabling', 'Wird aktiviert …')
                : t('settings.notif.push_enable', 'Aktivieren')}
            </button>
          )}
          {push.state === 'permission-denied' && (
            <span className="text-xs text-[hsl(var(--danger))]">
              {push.errorMessage ?? t('settings.notif.push_denied', 'Browser blockiert.')}
            </span>
          )}
          {push.state === 'error' && (
            <span className="text-xs text-[hsl(var(--danger))]">{push.errorMessage}</span>
          )}
          {push.isSubscribed && (
            <span className="text-xs text-[hsl(var(--positive))]">
              ✓ {t('settings.notif.push_active', 'Aktiv')}
            </span>
          )}
        </div>
      </div>
      <Toggle
        checked={briefing}
        onChange={setBriefing}
        title={t('settings.notif.briefing', 'Morning Briefing E-Mail')}
        description={t(
          'settings.notif.briefing_hint',
          'Tägliche Zusammenfassung um 06:00 Wien-Zeit.',
        )}
      />
      <Toggle
        checked={smsReminder}
        onChange={setSmsReminder}
        title={t('settings.notif.sms', 'Termin-SMS-Erinnerung')}
        description={t(
          'settings.notif.sms_hint',
          'Klient erhält SMS 2h vor jedem Termin.',
        )}
      />
      <Toggle
        checked={assignment}
        onChange={setAssignment}
        title={t('settings.notif.assignment', 'Neue Zuweisungen')}
        description={t(
          'settings.notif.assignment_hint',
          'In-App-Benachrichtigung bei neuer Klienten- oder Property-Zuweisung.',
        )}
      />
      <p className="pt-2 text-xs text-ink-3">
        {t('settings.notif.persist_note', 'Änderungen werden in v2 persistiert (Backend-Toggle existiert).')}
      </p>
    </div>
  );
}

function LanguagePanel() {
  const { t, i18n } = useTranslation();
  const cur = i18n.resolvedLanguage ?? DEFAULT_LOCALE;
  // Single source of truth — see packages/shared/src/locales.ts. Only 'live'
  // locales appear; the rest of the 30 are declared but not yet translated.
  // Registry order (German first) on purpose — the switcher leads with the
  // firm's six main markets because that mirrors the public site, but this
  // is a German-first internal setting screen.
  const langs = liveLocales();
  const planned = LOCALES.length - langs.length;

  return (
    <div className="fm-card p-6 space-y-3">
      <p className="text-sm text-ink-2">
        {t('settings.language.note', 'Wählen Sie Ihre bevorzugte Anzeigesprache:')}
      </p>
      <div className="flex flex-wrap gap-2">
        {langs.map((l) => (
          <button
            key={l.code}
            type="button"
            dir={l.dir}
            // The active chip was distinguishable by colour alone; a screen
            // reader had no way to tell which language is in use.
            aria-pressed={cur === l.code}
            onClick={() => {
              // setLocale, not i18n.changeLanguage: it loads the locale chunk
              // and refuses codes that have no translations yet. The old
              // localStorage.setItem('language', …) here was dead — the
              // detector reads 'i18nextLng' and nothing ever read 'language'.
              void setLocale(l.code);
              toast.success(
                t('settings.language.saved', 'Sprache: {{name}}', { name: l.nativeName }),
              );
            }}
            className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition ${
              cur === l.code
                ? 'bg-ink text-paper'
                : 'border border-line bg-card text-ink hover:bg-paper-2'
            }`}
          >
            <span aria-hidden className="text-[15px] leading-none">{l.flag}</span>
            {l.nativeName}
          </button>
        ))}
      </div>
      {planned > 0 && (
        <p className="pt-1 text-xs text-ink-3">
          {t('settings.language.planned', '{{count}} weitere Sprachen sind in Arbeit.', {
            count: planned,
          })}
        </p>
      )}
    </div>
  );
}

function ThemePanel() {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const update = useUpdateAppearance();
  const uploadBg = useUploadBackground();
  const bgInputRef = useRef<HTMLInputElement | null>(null);
  const current: AppearancePrefs = parseAppearance(profile?.ui_preferences);

  const set = async (patch: AppearancePrefs) => {
    try {
      await update.mutateAsync({ ...current, ...patch });
    } catch (cause) {
      toast.error((cause as Error).message);
    }
  };

  const applyPreset = async (presetId: string) => {
    const p = APPEARANCE_PRESETS.find((x) => x.id === presetId);
    if (!p) return;
    try {
      await update.mutateAsync({ ...p, presetId });
      toast.success(t('settings.appearance.preset_applied', 'Voreinstellung übernommen'));
    } catch (cause) {
      toast.error((cause as Error).message);
    }
  };

  const onBackgroundPick = () => bgInputRef.current?.click();
  const onBgFileChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      await uploadBg.mutateAsync(file);
      toast.success(t('settings.appearance.bg_saved', 'Hintergrundbild gespeichert'));
    } catch (cause) {
      toast.error((cause as Error).message);
    }
  };

  return (
    <div className="space-y-5">
      {/* Presets */}
      <div className="fm-card p-5">
        <p className="mb-3 text-xs font-semibold uppercase text-ink-3 tracking-[0.08em]">
          {t('settings.appearance.presets', 'Voreinstellungen')}
        </p>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
          {APPEARANCE_PRESETS.map((p) => {
            const active = current.presetId === p.id;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => applyPreset(p.id)}
                className={`flex flex-col items-start gap-1.5 rounded-md border px-3 py-3 text-start transition-colors ${
                  active
                    ? 'border-[hsl(var(--brand-gold))] bg-[hsl(var(--brand-gold))]/10'
                    : 'border-line bg-card hover:bg-paper-2'
                }`}
              >
                <span className="flex items-center gap-1.5">
                  <span
                    className="inline-block h-3.5 w-3.5 rounded-full border border-line"
                    style={{ background: p.accentColor ?? '#000' }}
                  />
                  <span className="font-display text-base text-ink">{p.name}</span>
                </span>
                <span className="text-[11px] leading-snug text-ink-3">{p.description}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Customiser */}
      <div className="fm-card space-y-4 p-5">
        <Row label={t('settings.appearance.theme', 'Theme')}>
          <ChipGroup
            value={current.theme ?? 'auto'}
            options={[
              ['light', t('settings.appearance.light', 'Hell')],
              ['dark', t('settings.appearance.dark', 'Dunkel')],
              ['auto', t('settings.appearance.auto', 'Auto')],
            ]}
            onChange={(v) => set({ theme: v as 'light' | 'dark' | 'auto' })}
          />
        </Row>
        <Row label={t('settings.appearance.accent', 'Akzent-Farbe')}>
          <AccentPicker
            value={current.accentColor ?? '#1a3a5c'}
            onChange={(v) => set({ accentColor: v })}
            background={
              current.background?.kind === 'solid' && current.background?.color
                ? current.background.color
                : current.theme === 'dark'
                  ? '#0a0a0a'
                  : '#ffffff'
            }
            t={t}
          />
        </Row>
        <Row label={t('settings.appearance.pattern', 'Muster')}>
          <ChipGroup
            value={current.pattern ?? 'none'}
            options={[
              ['none', t('settings.appearance.pattern_none', 'Keins')],
              ['dots', t('settings.appearance.pattern_dots', 'Punkte')],
              ['grid', t('settings.appearance.pattern_grid', 'Gitter')],
              ['linen', t('settings.appearance.pattern_linen', 'Leinen')],
              ['topo', t('settings.appearance.pattern_topo', 'Topo')],
            ]}
            onChange={(v) =>
              set({ pattern: v as 'none' | 'dots' | 'grid' | 'linen' | 'topo' })
            }
          />
        </Row>
        <Row label={t('settings.appearance.font_display', 'Schrift Display')}>
          <Select
            value={current.fontDisplay ?? 'Fraunces'}
            onChange={(v) =>
              set({ fontDisplay: v as 'Fraunces' | 'Lora' | 'Inter' })
            }
            options={['Fraunces', 'Lora', 'Inter']}
          />
        </Row>
        <Row label={t('settings.appearance.font_ui', 'Schrift UI')}>
          <Select
            value={current.fontUI ?? 'Inter'}
            onChange={(v) => set({ fontUI: v as 'Inter' | 'system' })}
            options={['Inter', 'system']}
          />
        </Row>
        <Row label={t('settings.appearance.icon_weight', 'Icon-Stärke')}>
          <ChipGroup
            value={current.iconWeight ?? 'regular'}
            options={[
              ['thin', t('settings.appearance.icon_thin', 'Dünn')],
              ['regular', t('settings.appearance.icon_regular', 'Normal')],
              ['bold', t('settings.appearance.icon_bold', 'Fett')],
            ]}
            onChange={(v) => set({ iconWeight: v as 'thin' | 'regular' | 'bold' })}
          />
        </Row>
        <Row label={t('settings.appearance.background', 'Hintergrund')}>
          <div className="flex flex-wrap items-center gap-2">
            <ChipGroup
              value={current.background?.kind ?? 'none'}
              options={[
                ['none', t('settings.appearance.bg_none', 'Standard')],
                ['solid', t('settings.appearance.bg_solid', 'Einfarbig')],
                ['gradient', t('settings.appearance.bg_gradient', 'Verlauf')],
                ['image', t('settings.appearance.bg_image', 'Bild')],
              ]}
              onChange={(v) =>
                set({
                  background: {
                    ...(current.background ?? {}),
                    kind: v as 'none' | 'solid' | 'gradient' | 'image',
                  },
                })
              }
            />
            {current.background?.kind === 'solid' && (
              <input
                type="color"
                value={current.background?.color ?? '#ffffff'}
                onChange={(e) =>
                  set({ background: { ...(current.background ?? {}), kind: 'solid', color: e.target.value } })
                }
                className="h-8 w-12 cursor-pointer rounded-md border border-line"
              />
            )}
            {current.background?.kind === 'image' && (
              <>
                <input
                  ref={bgInputRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={onBgFileChosen}
                />
                <button
                  type="button"
                  onClick={onBackgroundPick}
                  disabled={uploadBg.isPending}
                  className="inline-flex items-center gap-1.5 rounded-md border border-line bg-card px-3 py-1.5 text-xs font-semibold text-ink hover:bg-paper-2 disabled:opacity-50"
                >
                  <Camera className="h-3.5 w-3.5" />
                  {t('settings.appearance.bg_upload', 'Bild hochladen')}
                </button>
                <label className="flex items-center gap-2 text-xs text-ink-3">
                  {t('settings.appearance.bg_opacity', 'Transparenz')}
                  <input
                    type="range"
                    min={0}
                    max={1}
                    step={0.05}
                    value={current.background?.opacity ?? 0.15}
                    onChange={(e) =>
                      set({
                        background: {
                          ...(current.background ?? {}),
                          kind: 'image',
                          opacity: parseFloat(e.target.value),
                        },
                      })
                    }
                  />
                  <span className="font-mono text-[11px]">
                    {Math.round((current.background?.opacity ?? 0.15) * 100)}%
                  </span>
                </label>
              </>
            )}
          </div>
        </Row>
      </div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-2 border-b border-line pb-3 last:border-b-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between">
      <span className="text-xs font-semibold uppercase text-ink-3 tracking-[0.08em]">
        {label}
      </span>
      <div className="flex flex-wrap items-center gap-2">{children}</div>
    </div>
  );
}

function ChipGroup({
  value,
  options,
  onChange,
}: {
  value: string;
  options: Array<[string, string]>;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1 rounded-md bg-paper-2 p-0.5">
      {options.map(([val, label]) => (
        <button
          key={val}
          type="button"
          onClick={() => onChange(val)}
          className={`rounded px-2.5 py-1 text-xs font-semibold transition-colors ${
            value === val ? 'bg-card text-ink shadow-soft' : 'text-ink-3 hover:text-ink'
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function AccentPicker({
  value,
  onChange,
  background,
  t,
}: {
  value: string;
  onChange: (v: string) => void;
  background: string;
  t: (k: string, fb: string) => string;
}) {
  const ratio = contrastRatio(value, background);
  const ratioVsWhite = contrastRatio(value, '#ffffff');
  const passesAA = ratio >= 4.5;
  const buttonReadable = ratioVsWhite >= 4.5;
  const suggested = suggestReadableAccent(value, background, 4.5);

  return (
    <div className="flex flex-col items-end gap-1">
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 w-16 cursor-pointer rounded-md border border-line bg-card"
      />
      <div className="flex items-center gap-2 text-[10.5px] text-ink-3">
        <span>
          {t('settings.appearance.contrast', 'Kontrast')}:{' '}
          <span className={passesAA ? 'text-[hsl(var(--positive))]' : 'text-[hsl(var(--warning))]'}>
            {ratio.toFixed(2)}:1
          </span>
        </span>
        {!buttonReadable && (
          <span className="text-[hsl(var(--warning))]">
            · {t('settings.appearance.contrast_warn_text', 'Weiß auf Akzent schwer lesbar')}
          </span>
        )}
        {!passesAA && suggested !== value && (
          <button
            type="button"
            onClick={() => onChange(suggested)}
            className="rounded border border-line bg-card px-2 py-0.5 font-medium text-ink hover:bg-paper-2"
          >
            {t('settings.appearance.contrast_suggest', 'Vorschlag')}: {suggested}
          </button>
        )}
      </div>
    </div>
  );
}

function Select({
  value,
  options,
  onChange,
}: {
  value: string;
  options: string[];
  onChange: (v: string) => void;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="rounded-md border border-line bg-card px-2 py-1 text-sm text-ink outline-none focus:border-gold"
    >
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
  );
}

function SecurityPanel() {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const changePassword = useChangePassword();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const passwordsMatch = newPassword.length > 0 && newPassword === confirmPassword;
  const tooShort = newPassword.length > 0 && newPassword.length < 8;
  const canSubmit =
    currentPassword.length > 0 &&
    newPassword.length >= 8 &&
    passwordsMatch &&
    !changePassword.isPending;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (newPassword === currentPassword) {
      setErr(t('settings.security.err.same_as_current', 'Das neue Passwort muss sich vom aktuellen unterscheiden.'));
      return;
    }
    if (!passwordsMatch) {
      setErr(t('settings.security.err.mismatch', 'Die neuen Passwörter stimmen nicht überein.'));
      return;
    }
    try {
      await changePassword.mutateAsync({ currentPassword, newPassword });
      toast.success(t('settings.security.success', 'Passwort wurde erfolgreich geändert.'));
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (cause) {
      const msg = (cause as Error).message;
      // Map server error codes to friendly German strings
      if (msg === 'invalid_current_password') {
        setErr(t('settings.security.err.wrong_current', 'Aktuelles Passwort ist falsch.'));
      } else if (msg === 'new_password_must_differ') {
        setErr(t('settings.security.err.same_as_current', 'Das neue Passwort muss sich vom aktuellen unterscheiden.'));
      } else {
        setErr(msg);
      }
    }
  };

  return (
    <div className="space-y-4">
      <div className="fm-card p-6 space-y-4">
        <header>
          <h3 className="font-display text-lg text-ink">
            {t('settings.security.password.title', 'Passwort ändern')}
          </h3>
          <p className="mt-1 text-sm text-ink-2">
            {profile?.role === 'admin'
              ? t(
                  'settings.security.password.subtitle_admin',
                  'Neues Admin-Passwort setzen. Wirkt sofort und überschreibt den Worker-Secret-Eintrag.',
                )
              : t(
                  'settings.security.password.subtitle_user',
                  'Neues persönliches Anmeldepasswort setzen. Wirkt sofort beim nächsten Login.',
                )}
          </p>
        </header>

        <form className="space-y-4" onSubmit={onSubmit}>
          <PasswordField
            label={t('settings.security.password.current', 'Aktuelles Passwort')}
            value={currentPassword}
            onChange={setCurrentPassword}
            autoComplete="current-password"
            required
          />
          <PasswordField
            label={t('settings.security.password.new', 'Neues Passwort')}
            value={newPassword}
            onChange={setNewPassword}
            autoComplete="new-password"
            required
            helperText={
              !tooShort
                ? t('settings.security.password.hint_min', 'Mindestens 8 Zeichen.')
                : undefined
            }
            error={tooShort ? t('settings.security.err.too_short', 'Mindestens 8 Zeichen.') : null}
          />
          <PasswordField
            label={t('settings.security.password.confirm', 'Neues Passwort bestätigen')}
            value={confirmPassword}
            onChange={setConfirmPassword}
            autoComplete="new-password"
            required
            error={
              confirmPassword.length > 0 && !passwordsMatch
                ? t('settings.security.err.mismatch', 'Die neuen Passwörter stimmen nicht überein.')
                : null
            }
          />

          {err && (
            <p className="rounded-md border border-[hsl(var(--danger))]/30 bg-[hsl(var(--danger-bg))] px-3 py-2 text-sm text-[hsl(var(--danger))]">
              {err}
            </p>
          )}

          <button
            type="submit"
            disabled={!canSubmit}
            className="rounded-md bg-ink px-4 py-2.5 text-sm font-semibold text-paper shadow-soft transition-all hover:bg-ink/90 disabled:opacity-60"
          >
            {changePassword.isPending
              ? t('settings.security.password.saving', 'Wird gespeichert …')
              : t('settings.security.password.submit', 'Passwort ändern')}
          </button>
        </form>
      </div>

      <div className="fm-card p-6 space-y-2">
        <p className="text-xs uppercase tracking-[0.1em] text-gold-deep font-semibold">
          {t('settings.security.future_title', 'Geplant')}
        </p>
        <p className="text-sm text-ink-2">
          {t(
            'settings.security.note',
            '2-Faktor-Authentifizierung und aktive Sessions folgen in v2.',
          )}
        </p>
        <p className="text-xs text-ink-3">
          {t(
            'settings.security.audit_hint',
            'Verdächtige Aktivitäten sind im Admin-Bereich unter „Sicherheit" sichtbar.',
          )}
        </p>
      </div>
    </div>
  );
}

function ApiKeysPanel() {
  const { t } = useTranslation();
  return (
    <div className="fm-card p-6 space-y-3">
      <p className="text-sm text-ink-2">
        {t('settings.api.note', 'Status der konfigurierten Integrationen:')}
      </p>
      <ul className="space-y-2 text-sm">
        <ApiRow label="Twilio" hint="SMS-Versand" />
        <ApiRow label="Resend" hint="E-Mail-Versand + Marketing" />
        <ApiRow label="Anthropic Claude" hint="AI-Assistent" />
      </ul>
      <p className="pt-2 text-xs text-ink-3">
        {t(
          'settings.api.secrets_hint',
          'Secrets werden über Cloudflare Workers gesetzt (wrangler secret put).',
        )}
      </p>
    </div>
  );
}

function ApiRow({ label, hint }: { label: string; hint: string }) {
  return (
    <li className="flex items-center justify-between gap-3 rounded-md border border-line bg-paper-2 px-3 py-2.5">
      <div>
        <p className="font-semibold text-ink">{label}</p>
        <p className="text-xs text-ink-3">{hint}</p>
      </div>
      <span className="fm-pill fm-pill-pos">Konfiguriert</span>
    </li>
  );
}

function ExportPanel() {
  const { t } = useTranslation();
  return (
    <div className="fm-card p-6 space-y-3">
      <p className="text-sm text-ink-2">
        {t(
          'settings.export.note',
          'DSGVO-Export Ihrer eigenen Daten als CSV/JSON. Folgt in v2.',
        )}
      </p>
      <a
        href={`${import.meta.env.VITE_API_URL}/audit/export.csv`}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-2 rounded-md border border-line bg-card px-3 py-2 text-sm font-medium text-ink hover:bg-paper-2"
      >
        <Download className="h-3.5 w-3.5" />
        {t('settings.export.audit', 'Audit-Log CSV exportieren')}
      </a>
    </div>
  );
}

function Toggle({
  checked,
  onChange,
  title,
  description,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  title: string;
  description?: string;
}) {
  return (
    <label className="flex cursor-pointer items-center justify-between gap-4 rounded-md border border-line bg-paper-2 px-4 py-3">
      <div>
        <p className="font-medium text-ink">{title}</p>
        {description && <p className="text-xs text-ink-3">{description}</p>}
      </div>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 accent-[hsl(var(--brand-gold))]"
      />
    </label>
  );
}
